/**
 * Cancelling, and what it costs.
 *
 * ---------------------------------------------------------------------------
 * THE WINDOW IS THE WHOLE FEATURE
 * ---------------------------------------------------------------------------
 * "Cancel" is not one action. What it costs, who it affects and whether it can
 * be automated at all depend entirely on how far along the booking is, and the
 * customer has to be told which of those they are in *before* they confirm -
 * a screen that says "Cancel booking" and then charges a fee is a support
 * ticket and a one-star review.
 *
 *   A/B  nobody assigned yet     full refund, nothing to undo
 *   D    the professional is on their way   refund less the fee; they are
 *        freed immediately and are owed nothing, being salaried
 *   E    work has started        partial, and a person decides - not a
 *        percentage. This is the window that becomes a dispute.
 *   ops  we could not send anyone   full refund, and an apology, and ideally
 *        a rebooking offer. Never counted against the customer.
 *
 * There is deliberately no window C. The spec's lettering comes from the
 * booking lifecycle, not from this file, and renumbering it here would make
 * the two impossible to talk about together.
 */

import { appConfig } from '../services/config';
import { Booking, BookingStage } from './bookings';

/** What a cancellation in this window returns. */
export type RefundKind = 'full' | 'less-fee' | 'partial' | 'none';

export type CancelWindow = {
  key: 'A' | 'B' | 'D' | 'E' | 'ops';
  /** Shown as the sheet's heading. */
  title: string;
  /** One line under it: what happens to the money. */
  outcome: string;
  kind: RefundKind;
  /**
   * Percentage of the paid amount retained. Zero everywhere but window D.
   *
   * ---------------------------------------------------------------------------
   * A PERCENTAGE, AND `fee` IS GONE ON PURPOSE
   * ---------------------------------------------------------------------------
   * This was `fee: number`, a flat ₹49, and it was never what anybody was
   * charged. The server retains `booking.lateCancellationFeePercent` of the
   * payable amount - so the app promised ₹49 and a ₹2,000 booking cost ₹500.
   *
   * The field was not merely corrected to a percentage, it was RENAMED, so
   * that every place reading it had to be looked at. A `fee` that quietly
   * changed meaning from rupees to percent would have rendered "₹25" - a
   * smaller, more believable lie than the one being fixed.
   *
   * Rupees come from {@link feeFor}, which needs the amount and therefore
   * cannot be called without one.
   */
  feePercent: number;
  /**
   * Whether the app can settle the amount itself.
   *
   * False in window E, where the sheet must promise a review rather than a
   * number. Quoting "60%" and then refunding something else is worse than
   * quoting nothing.
   */
  automatic: boolean;
  /** The consequence for somebody else, stated plainly. */
  note?: string;
};

/**
 * The published promise, asked for rather than compiled in.
 *
 * ---------------------------------------------------------------------------
 * THESE USED TO BE CONSTANTS, AND ONE OF THEM WAS FALSE
 * ---------------------------------------------------------------------------
 * `CANCEL_FEE = 49` and `FREE_WINDOW_HOURS = 6` sat here, and the comment
 * beside them worried - correctly, and in writing - that "a customer who reads
 * one and is charged by another has been misled". That is exactly what was
 * happening: the server has never charged a flat fee. It retains a percentage,
 * ops sets it, and nothing ever told the app.
 *
 * Both now come from `GET /config`, which serves the same two settings the
 * cancellation path itself reads. The constants below survive only as the
 * value used before the server has answered, and they match the server's own
 * defaults so an app on the fallback still quotes what an unconfigured
 * deployment would charge.
 *
 * Read through `appConfig()` at CALL time, never destructured at module scope:
 * this file is imported at launch, long before the config request lands.
 */
const FALLBACK_FREE_WINDOW_HOURS = 6;
const FALLBACK_LATE_FEE_PERCENT = 25;

/** Hours before the slot within which cancelling is still free. */
export const freeWindowHours = (): number =>
  appConfig().freeCancellationHours ?? FALLBACK_FREE_WINDOW_HOURS;

/** Percentage of the paid amount retained on a late cancellation. */
export const lateFeePercent = (): number =>
  appConfig().lateCancellationFeePercent ?? FALLBACK_LATE_FEE_PERCENT;

/**
 * What a window's percentage actually costs on one booking, in whole rupees.
 *
 * Rounded, and never more than what was paid - a percentage of a small basket
 * can round to more than the basket after a discount, and a "refund" that
 * takes more than it returns is a support call.
 */
export const feeFor = (window: CancelWindow, paid: number): number =>
  Math.min(paid, Math.round((paid * window.feePercent) / 100));

/**
 * Whether the visit is still far enough away to cancel free of charge.
 *
 * `undefined` means the booking carries no machine-readable start - the
 * bundled examples, and every instant booking, which has no scheduled instant
 * to be six hours before. That is NOT treated as "inside the window": an
 * instant job that nobody has been assigned to yet costs nothing to cancel,
 * and charging a fee because a timestamp was missing is the worst way to be
 * wrong. Those fall through to the stage rules below, which is exactly what
 * they did before this function existed.
 */
export const insideFreeWindow = (
  slotStartAt: string | undefined,
  now: Date,
): boolean => {
  if (!slotStartAt) {
    return false;
  }
  const start = new Date(slotStartAt).getTime();
  if (Number.isNaN(start)) {
    return false;
  }
  return start - now.getTime() < freeWindowHours() * 60 * 60 * 1000;
};

/**
 * Which window a booking is in.
 *
 * Two rules, and the stage is only the second of them.
 *
 * The promise the app makes everywhere is about the CLOCK - "cancel or
 * reschedule at no cost up to six hours before the visit" - and the stage is
 * a poor proxy for it. A visit booked for Friday morning sits at `booked`
 * until dispatch runs, which may be an hour beforehand; under stage alone
 * that cancellation is free five minutes before the professional sets off,
 * which is not what was promised and not what operations can absorb.
 *
 * So: inside six hours of a scheduled start, the fee applies whatever the
 * stage says. Outside it - or where there is no timestamp to measure, see
 * `insideFreeWindow` - the stage rules stand as they were, because once
 * somebody is on their way or working the clock has stopped being the
 * question.
 *
 * `now` is passed in rather than read here so the caller decides what "now"
 * is, and so this stays a pure function a test can pin to a date.
 */
export const windowFor = (
  stage: BookingStage,
  slotStartAt?: string,
  now: Date = new Date(),
): CancelWindow => {
  /*
   * The late window, reached by the clock rather than by dispatch.
   *
   * Only applied to the two stages where nobody has set off yet. `onway` and
   * `service` already have their own, stricter outcomes below and must not be
   * softened into this one.
   */
  if (
    (stage === 'booked' || stage === 'assigned') &&
    insideFreeWindow(slotStartAt, now)
  ) {
    return {
      key: 'D',
      title: `Cancel - inside the ${freeWindowHours()}-hour window`,
      outcome: `You get a full refund less a ${lateFeePercent()}% late cancellation fee.`,
      kind: 'less-fee',
      feePercent: lateFeePercent(),
      automatic: true,
      note: `Free cancellation ended ${freeWindowHours()} hours before your visit.`,
    };
  }

  switch (stage) {
    case 'booked':
      return {
        key: 'A',
        title: 'Cancel this booking',
        outcome: 'You get a full refund. Nobody has been assigned yet.',
        kind: 'full',
        feePercent: 0,
        automatic: true,
      };
    case 'assigned':
      return {
        key: 'B',
        title: 'Cancel this booking',
        outcome: 'You get a full refund. No cancellation fee applies.',
        kind: 'full',
        feePercent: 0,
        automatic: true,
        note: 'Your professional is told straight away and freed up.',
      };
    case 'onway':
      return {
        key: 'D',
        title: 'Cancel - your professional is on the way',
        outcome: `You get a full refund less a ${lateFeePercent()}% late cancellation fee.`,
        kind: 'less-fee',
        feePercent: lateFeePercent(),
        automatic: true,
        note: 'They are already travelling to you, and are told immediately.',
      };
    case 'service':
      return {
        key: 'E',
        title: 'Stop this job',
        outcome:
          'Our team reviews what was done and confirms your refund within 24 hours.',
        kind: 'partial',
        feePercent: 0,
        automatic: false,
        note: 'Work has already started, so the amount is decided by a person.',
      };
    default:
      return {
        key: 'A',
        title: 'Cancel this booking',
        outcome: 'You get a full refund.',
        kind: 'full',
        feePercent: 0,
        automatic: true,
      };
  }
};

/** A completed booking is disputed, never cancelled. */
export const canCancel = (booking: Booking): boolean =>
  booking.status !== 'cancelled' && booking.stage !== 'completed';

/**
 * Whether this booking can still be moved to another slot.
 *
 * Stricter than `canCancel` by two stages. Once the professional is on their
 * way - or already working - there is no longer a future visit to move: the
 * visit is happening. Offering "Reschedule" at that point would either cancel
 * the current job under a friendlier word, or book a second one, and the
 * customer cannot tell which from the button.
 *
 * Deliberately NOT gated on the six-hour window. Rescheduling inside six
 * hours is allowed - it just costs the same fee cancelling does, which is
 * what the policy says and what the sheet quotes. Hiding the button would
 * leave someone whose plans changed at short notice with cancelling as their
 * only option, which is worse for them and worse for us.
 */
export const canReschedule = (booking: Booking): boolean =>
  canCancel(booking) &&
  booking.stage !== 'onway' &&
  booking.stage !== 'service';

/**
 * What comes back, in rupees.
 *
 * Returns null in window E on purpose: there is no honest number to show, and
 * the sheet renders a sentence instead of a figure.
 */
export const refundAmount = (
  booking: Booking,
  window: CancelWindow,
): number | null => {
  if (!window.automatic) {
    return null;
  }
  return Math.max(
    0,
    booking.payment.paid - feeFor(window, booking.payment.paid),
  );
};

export type CancelReason = {
  key: string;
  label: string;
};

/**
 * Why, in the customer's words.
 *
 * Asked for, never required - a cancel button that will not proceed until a
 * radio is picked is a button that gets pressed twice as often, from the wrong
 * screen, by somebody who has already given up on the flow. The list is short
 * and the last entry is an escape hatch.
 *
 * The two lists differ because the reasons genuinely differ: nobody cancels a
 * booking nobody is assigned to because "the professional has not arrived".
 */
export const reasonsFor = (window: CancelWindow): CancelReason[] =>
  window.key === 'D' || window.key === 'E'
    ? [
        { key: 'late', label: 'Taking longer than expected' },
        { key: 'wrong', label: 'Not the service I need' },
        { key: 'unhappy', label: 'Unhappy with how it is going' },
        { key: 'unavailable', label: 'I am no longer at home' },
        { key: 'other', label: 'Something else' },
      ]
    : [
        { key: 'plans', label: 'My plans changed' },
        { key: 'time', label: 'Booked the wrong time' },
        { key: 'price', label: 'Found it cheaper elsewhere' },
        { key: 'mistake', label: 'Booked by mistake' },
        { key: 'other', label: 'Something else' },
      ];
